<template>
  <div class="login-glass-overlay">
    <div class="login-glass-box">
      <img src="/logo2.png" alt="系统Logo" class="login-logo" />
      <h2 class="glass-title">{{ isRegisterMode ? '创建巡检员账号' : '登录系统' }}</h2>
      <p class="glass-subtitle">{{ isRegisterMode ? '注册并加入智能巡检网络' : '电力线路绝缘子缺陷 AI 智能巡检平台' }}</p>

      <div class="input-wrapper">
        <input v-if="!isRegisterMode" type="text" v-model="loginUsername" @keyup.enter="handleLogin" class="glass-input"
          placeholder="请输入用户名或邮箱" style="margin-bottom: 15px;" />
        <input v-if="!isRegisterMode" type="password" v-model="loginPwd" @keyup.enter="handleLogin" class="glass-input"
          placeholder="请输入密码" />

        <template v-if="isRegisterMode">
          <input type="text" v-model="regUsername" class="glass-input" placeholder="设置用户名" style="margin-bottom: 15px;" />
          <div class="email-row" style="margin-bottom: 15px;">
            <input type="email" v-model="regEmail" class="glass-input" placeholder="请输入邮箱" style="flex: 1; margin-bottom: 0;" />
            <button class="send-code-btn" @click="sendCode" :disabled="codeCooldown > 0 || isSendingCode">
              {{ isSendingCode ? '发送中...' : (codeCooldown > 0 ? `${codeCooldown}s` : '发送') }}
            </button>
          </div>
          <input type="text" v-model="regCode" class="glass-input" placeholder="输入收到的验证码" style="margin-bottom: 15px;" />
          <input type="password" v-model="regPwd" @keyup.enter="submitRegister" class="glass-input" placeholder="设置登录密码" />
        </template>
      </div>

      <button class="glass-primary-btn" @click="isRegisterMode ? submitRegister() : handleLogin()">
        <span>{{ isRegisterMode ? '立即注册' : '登入系统' }}</span>
      </button>

      <div class="error-wrapper" v-if="loginError" :class="{ 'fading': errorFading }">
        <p class="glass-error-text">{{ loginError }}</p>
      </div>

      <div class="mode-switch">
        <span v-if="!isRegisterMode">没有账号？<a href="#" @click.prevent="isRegisterMode = true">注册成为巡检员</a></span>
        <span v-else>已有账号？<a href="#" @click.prevent="isRegisterMode = false">返回登录</a></span>
      </div>
    </div>

    <div class="beian-info">
      <a href="https://beian.miit.gov.cn/" target="_blank">ICP备案号：鄂ICP备2026015730号-1</a><br>
      <a href="https://beian.mps.gov.cn/#/query/webSearch?code=42050002420970" rel="noreferrer" target="_blank">
        <img src="/beian.png" width="11" />
        鄂公网安备42050002420970号
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useAuth } from '../composables/useAuth';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const { loginUsername, loginPwd, loginError, handleLogin, handleRegister } = useAuth();

const isRegisterMode = ref(false);
const regUsername = ref('');
const regEmail = ref('');
const regCode = ref('');
const regPwd = ref('');
const codeCooldown = ref(0);
const isSendingCode = ref(false);
const errorFading = ref(false);
let errorTimer = null;

// 错误提示 5 秒自动消失
watch(loginError, (val) => {
  if (val) {
    errorFading.value = false;
    clearTimeout(errorTimer);
    errorTimer = setTimeout(() => {
      errorFading.value = true;
      setTimeout(() => { loginError.value = ''; errorFading.value = false; }, 400);
    }, 5000);
  }
});

const sendCode = async () => {
  if (!regEmail.value || !EMAIL_REGEX.test(regEmail.value)) {
    loginError.value = '请先输入正确的邮箱';
    return;
  }
  
  isSendingCode.value = true;
  try {
    const res = await fetch('/api/send_code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: regEmail.value })
    });
    const data = await res.json();
    if (data.status === 'success') {
      codeCooldown.value = 60;
      const timer = setInterval(() => {
        codeCooldown.value--;
        if (codeCooldown.value <= 0) clearInterval(timer);
      }, 1000);
    } else {
      loginError.value = data.message || '发送失败';
    }
  } catch (err) {
    loginError.value = '邮件服务暂时不可用';
  } finally {
    isSendingCode.value = false;
  }
};

const submitRegister = async () => {
  const success = await handleRegister(regUsername.value, regEmail.value, regCode.value, regPwd.value);
  if (success) {
    alert('注册成功！已自动填入登录框，请点击登录。');
    isRegisterMode.value = false;
  }
};
</script>

<style scoped>
/* 深色科技感安全网关 */
.login-glass-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0f1923 0%, #1a2a3a 50%, #0d1b2a 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(10px);
}

.login-glass-box {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  /* 修复输入框的边框遮挡备案信息 padding:30px->40px */
  padding: 30px;
  width: 90%;
  max-width: 420px;
  text-align: center;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.2);
  color: white;
  animation: fadeInDown 0.6s ease;
}

@keyframes fadeInDown {
  0% {
    opacity: 0;
    transform: translateY(-30px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-logo {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  margin-bottom: 10px;
  animation: logoPulse 3s ease-in-out infinite;
}

@keyframes logoPulse {

  0%,
  100% {
    box-shadow: 0 0 20px rgba(52, 152, 219, 0.3);
  }

  50% {
    box-shadow: 0 0 40px rgba(52, 152, 219, 0.6);
  }
}

.glass-title {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 1px;
  margin: 10px 0 5px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.glass-subtitle {
  font-size: 13px;
  color: #ecf0f1;
  margin-bottom: 30px;
  opacity: 0.8;
}

.input-wrapper {
  margin-bottom: 25px;
}

.glass-input {
  width: 80%;
  box-sizing: border-box;
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 16px;
  text-align: center;
  outline: none;
  transition: var(--transition-normal);
}

.glass-input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.glass-input:focus {
  background: rgba(255, 255, 255, 0.25);
  border-color: #fff;
  box-shadow: 0 0 15px rgba(52, 152, 219, 0.3);
}

.glass-primary-btn {
  width: 80%;
  padding: 15px;
  border-radius: var(--radius-md);
  border: none;
  background: linear-gradient(135deg, var(--color-primary-dark), var(--color-primary));
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);
  transition: var(--transition-normal);
}

.glass-primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(52, 152, 219, 0.5);
}

.glass-primary-btn:active {
  transform: translateY(1px);
}

.error-wrapper {
  margin-top: 20px;
  background: rgba(231, 76, 60, 0.2);
  border-radius: 8px;
  padding: 10px;
  opacity: 1;
  transition: opacity 0.4s ease;
}

.error-wrapper.fading {
  opacity: 0;
}

.glass-error-text {
  color: #ff7675;
  font-size: 14px;
  margin: 0;
  font-weight: 500;
}

.mode-switch {
  /* 修复输入框的边框遮挡备案信息 margin-top: 25px -> 20px */
  margin-top: 20px;
  font-size: 13px;
  color: #ecf0f1;
}

.email-row {
  display: flex;
  gap: 10px;
  align-items: stretch; /* 让子元素高度一致 */
  width: 80%;
  margin: 0 auto;
}

.email-row .glass-input {
  flex: 1;
  width: auto;
  min-width: 0;
  margin-bottom: 0 !important; /* 覆盖之前的 margin-bottom */
}

.send-code-btn {
  white-space: nowrap;
  padding: 0 15px; /* 高度由 stretch 决定，只需横向 padding */
  min-width: 100px;
  border-radius: 12px; /* 与输入框一致 */
  border: 1px solid rgba(255,255,255,0.4);
  background: rgba(255,255,255,0.15);
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 480px) {
  .email-row {
    width: 80%;
  }
  .send-code-btn {
    padding: 0 10px;
    min-width: 60px;
    font-size: 13px;
  }
}

.send-code-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mode-switch a {
  color: var(--color-primary-light);
  text-decoration: none;
  font-weight: bold;
  margin-left: 5px;
}

.mode-switch a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .login-glass-box {
    /* 修复输入框的边框遮挡备案信息 padding:40px 20px -> 20px 20px; */
    padding: 20px 20px !important;
    width: 65% !important;
  }

  .login-logo {
    width: 70px !important;
    height: 70px !important;
  }

  .glass-title {
    font-size: 20px !important;
  }

  .glass-subtitle {
    font-size: 12px !important;
    margin-bottom: 20px !important;
  }

  .glass-input {
    padding: 14px 15px !important;
    font-size: 15px !important;
  }

  .glass-primary-btn {
    padding: 14px !important;
    font-size: 15px !important;
  }

  .beian-info {
    bottom: 15px !important;
    font-size: 11px !important;
  }
}

.beian-info {
  position: fixed;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  z-index: 9998;
}

.beian-info a {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
}

.beian-info a:hover {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
}

.beian-info a:visited {
  color: rgba(255, 255, 255, 0.6);
}
</style>
